import React from "react";
import { useForm } from "react-hook-form";
import {
  Input,
  SelectWithRadio,
  SelectWithCheckbox,
  TextArea,
} from "@/components/form";
import countriesData from "../../../assets/countries.json";
import { Client } from "@/types/client.model";
import { capitalizeFirstLetter } from "@/utils/constants";
import { useAppSelector } from "@/hooks";
import { selectClientLoading } from "@/store/slices/clientSlice";

interface Country {
  name: string;
  code: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ClientFormData {
  id?: string;
  first_name: string;
  last_name: string;
  name?: string; // For backward compatibility
  org_name: string;
  email: string;
  cc?: string[];
  bcc?: string[];
  contact_number: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  location?: string;
  status: "active" | "inactive" | "pending";
}

interface ClientFormProps {
  onSubmit: (data: ClientFormData, meta: { emailChanged: boolean }) => void;
  initialData?: Client;
  onCancel: () => void;
  viewMode?: boolean;
}

const ClientForm: React.FC<ClientFormProps> = ({
  onSubmit,
  initialData,
  onCancel,
  viewMode = false,
}) => {
  const [canEditEmail, setCanEditEmail] = React.useState(false);
  // Set default values based on initialData if it exists
  let defaultValues: Partial<ClientFormData> = {
    first_name: "",
    last_name: "",
    org_name: "",
    email: "",
    cc: [],
    bcc: [],
    contact_number: "",
    website: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    location: "",
    status: "active",
  };

  if (initialData) {
    defaultValues = {
      id: initialData.id,
      first_name: initialData.first_name,
      last_name: initialData.last_name,
      org_name: initialData.org_name,
      email: initialData.email,
      cc: initialData.cc || [],
      bcc: initialData.bcc || [],
      contact_number: initialData.contact_number,
      website: initialData.website || "",
      address: initialData.address || "",
      city: initialData.city || "",
      state: initialData.state || "",
      country: initialData.country || "",
      pincode: initialData.pincode || "",
      location: initialData.location || "",
      status: initialData.status,
    };
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
  } = useForm<ClientFormData>({
    mode: "all",
    defaultValues,
    criteriaMode: "firstError",
    reValidateMode: "onChange",
  });

  // Track if the form has been modified
  const [isFormModified, setIsFormModified] = React.useState(false);

  // Watch all form values to detect changes
  const formValues = watch();

  // Trigger validation immediately when the form is mounted in edit mode
  React.useEffect(() => {
    if (initialData && !viewMode) {
      // Use setTimeout to ensure validation runs after the component has fully mounted
      // This helps ensure all field registrations are complete
      setTimeout(() => {
        trigger();
      }, 0);
    }
  }, [initialData, trigger, viewMode]);

  // Check if form values have changed from initial data
  React.useEffect(() => {
    if (initialData) {
      // Compare current form values with initial data
      const hasChanged =
        formValues.first_name !== initialData.first_name ||
        formValues.last_name !== initialData.last_name ||
        formValues.org_name !== initialData.org_name ||
        formValues.email !== initialData.email ||
        JSON.stringify(formValues.cc || []) !==
          JSON.stringify(initialData.cc || []) ||
        JSON.stringify(formValues.bcc || []) !==
          JSON.stringify(initialData.bcc || []) ||
        formValues.contact_number !== initialData.contact_number ||
        formValues.website !== (initialData.website || "") ||
        formValues.address !== (initialData.address || "") ||
        formValues.city !== (initialData.city || "") ||
        formValues.state !== (initialData.state || "") ||
        formValues.country !== (initialData.country || "") ||
        formValues.pincode !== (initialData.pincode || "") ||
        formValues.location !== (initialData.location || "") ||
        formValues.status !== initialData.status;

      setIsFormModified(hasChanged);
    }
  }, [formValues, initialData]);

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Transform countries data to match the expected format
  const countryOptions = [
    { value: "", label: "Select a country" },
    ...(countriesData as Country[]).map((country) => ({
      value: country.code,
      label: country.name,
    })),
  ];

  // Get loading state from Redux store
  const isLoading = useAppSelector(selectClientLoading);

  // Prepare data for submission
  const onFormSubmit = (data: ClientFormData) => {
    // Combine first and last name for backward compatibility
    const fullName = `${data.first_name} ${data.last_name}`.trim();
    const emailChanged = !!initialData && data.email !== initialData.email;

    onSubmit(
      {
        ...data,
        name: fullName,
      },
      { emailChanged }
    );
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <div className="mb-5 px-4 py-2 overflow-y-auto max-h-[calc(100vh-200px)]">
        <p className="text-[14px] text-[#6C6C6C] mb-2">
          <span className="text-[#DC2626]">*</span> indicates mandatory fields
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First name"
            id="first_name"
            placeholder="Enter first name"
            required
            autoFocus={!viewMode && !initialData}
            error={errors.first_name?.message as string}
            readOnly={viewMode}
            maxLength={25}
            {...register("first_name", {
              required: !viewMode ? "First name is required" : false,
            })}
          />

          <Input
            label="Last name"
            id="last_name"
            placeholder="Enter last name"
            required
            error={errors.last_name?.message as string}
            readOnly={viewMode}
            maxLength={25}
            {...register("last_name", {
              required: !viewMode ? "Last name is required" : false,
            })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <Input
              label="Organization name"
              id="org_name"
              placeholder="Enter organization name"
              required
              error={errors.org_name?.message as string}
              readOnly={viewMode}
              maxLength={50}
              {...register("org_name", {
                required: !viewMode ? "Organization name is required" : false,
              })}
            />
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <label className="block text-[#1E1E1E] text-[14px] font-[500]">
                Email address
                {!viewMode && (
                  <span className="text-[#DC2626] ml-0.5">*</span>
                )}
              </label>
              {initialData && !viewMode && !canEditEmail && (
                <button
                  type="button"
                  className="text-xs text-[#6093A0] underline cursor-pointer"
                  onClick={() => setCanEditEmail(true)}
                >
                  Edit
                </button>
              )}
            </div>
            <Input
              label=""
              id="email"
              type="email"
              placeholder="Enter email address"
              required={false}
              error={errors.email?.message as string}
              readOnly={viewMode || (!!initialData && !canEditEmail)}
              maxLength={50}
              {...register("email", {
                required: !viewMode && !initialData ? "Email is required" : false,
              })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <SelectWithCheckbox
            label="CC"
            id="cc"
            options={(initialData?.cc || []).map((email) => ({
              value: email,
              label: email,
            }))}
            value={watch("cc") || []}
            onChange={(value) =>
              setValue("cc", value, {
                shouldValidate: true,
              })
            }
            placeholder="Add email, press Enter"
            disabled={viewMode}
            allowCustomInput
            hideOptionsDropdown
            customValueValidator={(value) => EMAIL_REGEX.test(value)}
            customValueErrorMessage="Please enter a valid email address"
          />

          <SelectWithCheckbox
            label="BCC"
            id="bcc"
            options={(initialData?.bcc || []).map((email) => ({
              value: email,
              label: email,
            }))}
            value={watch("bcc") || []}
            onChange={(value) =>
              setValue("bcc", value, {
                shouldValidate: true,
              })
            }
            placeholder="Add email, press Enter"
            disabled={viewMode}
            allowCustomInput
            hideOptionsDropdown
            customValueValidator={(value) => EMAIL_REGEX.test(value)}
            customValueErrorMessage="Please enter a valid email address"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Phone number"
            id="contact_number"
            placeholder="Enter phone number"
            required
            error={errors.contact_number?.message as string}
            readOnly={viewMode}
            {...register("contact_number", {
              required: !viewMode ? "Phone number is required" : false,
              maxLength: !viewMode
                ? {
                    value: 15,
                    message: "Phone number must be at most 15 characters",
                  }
                : undefined,
              // pattern: !viewMode
              //   ? {
              //       value: /^\+?[0-9]+$/, // Only one optional + at the beginning followed by digits
              //       message: "Invalid phone number format.",
              //     }
              //   : undefined,
            })}
          />

          <Input
            label="Website"
            id="website"
            placeholder="Enter website link"
            required={false}
            readOnly={viewMode}
            error={errors.website?.message as string}
            maxLength={50}
            {...register("website", {
              required: false,
              validate: !viewMode
                ? (value) => {
                    // Skip validation if empty and not required
                    if (!value && !viewMode) return true;
                  const websiteValue = value || "";

                    // Check for protocol (http:// or https://)
                  if (!/^https?:\/\//i.test(websiteValue)) {
                      return "URL must start with http:// or https://";
                    }

                    try {
                      // Use URL constructor to validate the URL
                    const url = new URL(websiteValue);

                      // Only allow HTTP or HTTPS protocols
                      if (
                        url.protocol !== "http:" &&
                        url.protocol !== "https:"
                      ) {
                        return "Only HTTP and HTTPS protocols are supported";
                      }

                      // Check for consecutive dots in domain
                      if (url.hostname.includes("..")) {
                        return "Invalid domain name (contains consecutive dots)";
                      }

                      // Check for valid domain structure
                      if (
                        !/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(
                          url.hostname
                        )
                      ) {
                        return "Invalid domain name format";
                      }

                      return true;
                    } catch (e) {
                      return "Invalid URL format";
                    }
                  }
                : undefined,
            })}
          />
        </div>

        {initialData && (
          <div className="mb-4">
            {viewMode ? (
              <Input
                label="Status"
                id="status"
                readOnly={true}
                value={capitalizeFirstLetter(watch("status")) || ""}
                {...register("status")}
              />
            ) : (
              <SelectWithRadio
                id="status"
                label="Status"
                placeholder="Select a status"
                options={statusOptions}
                value={watch("status") || "Active"}
                onChange={(value) =>
                  setValue(
                    "status",
                    value as "active" | "inactive" | "pending",
                    {
                      shouldValidate: true,
                    }
                  )
                }
              />
            )}
          </div>
        )}

        <TextArea
          label="Address"
          id="address"
          placeholder="Enter address"
          readOnly={viewMode}
          rows={3}
          maxLength={500}
          resizable={false}
          {...register("address")}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="City"
            id="city"
            placeholder="Enter city"
            readOnly={viewMode}
            maxLength={50}
            {...register("city")}
          />

          <Input
            label="State"
            id="state"
            placeholder="Enter state"
            readOnly={viewMode}
            maxLength={50}
            {...register("state")}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {viewMode ? (
            <Input
              label="Country"
              id="country"
              readOnly={true}
              value={watch("country") || ""}
              {...register("country")}
            />
          ) : (
            <SelectWithRadio
              label="Country"
              id="country"
              options={countryOptions}
              value={watch("country") || ""}
              onChange={(value) => setValue("country", value)}
              placeholder="Select a country"
              // maxHeight="200px"
            />
          )}

          <Input
            label="ZIP code"
            id="pincode"
            placeholder="Enter ZIP code"
            readOnly={viewMode}
            error={errors.pincode?.message as string}
            {...register("pincode", {
              maxLength: {
                value: 9,
                message: "ZIP code must be at most 6 characters",
              },
              pattern: {
                value: /^[0-9]{6,9}$/,
                message: "ZIP code must be a valid number",
              },
            })}
          />
        </div>
      </div>
      {!viewMode && (
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 rounded-b-[10px]">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-[14px] w-[147px] text-gray-700 bg-[#F5F5F5] rounded-md hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="w-[147px] h-[42px] bg-[#73b5c5] text-white text-[14px] font-[500] border-none rounded-[4px] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed flex items-center justify-center"
            disabled={!isValid || isLoading || (initialData && !isFormModified)}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {initialData ? "Updating..." : "Adding..."}
              </>
            ) : (
              <>{initialData ? "Update" : "Add Client"}</>
            )}
          </button>
        </div>
      )}
    </form>
  );
};

export default ClientForm;
